import { ApolloError, DocumentNode, useQuery } from "@apollo/client";
import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  ALL_PRODUCTS_QUERY,
  ALL_VENDORS_QUERY,
  COUNTRY_QUERY,
  GET_FAI_DEPARTMENTS_QUERY,
  INDUSTRIES_QUERY,
  NAICS_QUERY,
  PRODUCT_ATTRIBUTE_QUERY,
  PRODUCT_CATEGORY_QUERY,
  REGION_QUERY,
  SIC_QUERY,
  SPEND_CATEGORY_QUERY,
  STATE_QUERY,
} from "../../apis/taxonomy/queries";
import {
  HgCountry,
  HgFaiDepartment,
  HgFaiRole,
  HgIndustry,
  HgNaics,
  HgProductAttribute,
  HgProductCategory,
  HgRegion,
  HgSic,
  HgSpendCategory,
  HgState,
  HgVendor,
  ProductsQueryPayload,
} from "../../types/taxonomy";
import { TaxonomyContextType } from "./types";
import { TAXONOMY_CLIENT } from "../../utils/consts";
type GenericVariablesObject = Record<string, unknown>;

const sortData = <T,>(
  data: T[],
  key: keyof T & string,
  direction: "asc" | "desc"
): T[] => {
  if (!data?.length) return data || [];

  return [...data].sort((a, b) => {
    const aValue = String(a[key] || "");
    const bValue = String(b[key] || "");

    return direction === "asc"
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });
};

interface TaxonomyQueryResult<T> {
  data: T[];
  loading: boolean;
  error?: ApolloError;
}

const useTaxonomyQuery = <T,>(
  query: DocumentNode,
  queryName: string,
  clientSideSorting?: {
    key: string;
    direction: "asc" | "desc";
  },
  variables?: GenericVariablesObject
): TaxonomyQueryResult<T> => {
  const {
    data: rawData,
    loading,
    error,
  } = useQuery(query, {
    context: { clientName: TAXONOMY_CLIENT },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-only",
    variables,
  });

  const data = rawData?.[queryName]?.data || [];

  if (clientSideSorting?.key && data?.length) {
    const sortedData = sortData(
      data,
      clientSideSorting.key as keyof T & string,
      clientSideSorting.direction
    );
    return { data: sortedData, loading, error };
  }

  return { data, loading, error };
};

const useTaxonomyContextFactory = () => {
  const { data: products, loading: productsLoading } =
    useQuery<ProductsQueryPayload>(ALL_PRODUCTS_QUERY, {
      context: {
        clientName: TAXONOMY_CLIENT,
      },
      fetchPolicy: "no-cache",
    });

  const { data: vendors, loading: vendorsLoading } = useTaxonomyQuery<HgVendor>(
    ALL_VENDORS_QUERY,
    "vendor"
  );

  const { data: hgIndustries, loading: hgIndustriesLoading } =
    useTaxonomyQuery<HgIndustry>(INDUSTRIES_QUERY, "industry");
  const { data: naics, loading: naicsLoading } = useTaxonomyQuery<HgNaics>(
    NAICS_QUERY,
    "naics2012"
  );
  const { data: sic, loading: sicLoading } = useTaxonomyQuery<HgSic>(
    SIC_QUERY,
    "sicUS1987"
  );
  const { data: countries, loading: countriesLoading } =
    useTaxonomyQuery<HgCountry>(COUNTRY_QUERY, "country");
  const { data: regions, loading: regionsLoading } = useTaxonomyQuery<HgRegion>(
    REGION_QUERY,
    "region",
    {
      key: "name",
      direction: "asc",
    }
  );
  const { data: states, loading: statesLoading } = useTaxonomyQuery<HgState>(
    STATE_QUERY,
    "state"
  );
  const {
    data: spendCategories,
    loading: spendCategoriesLoading,
    error: spendCategoriesError,
  } = useTaxonomyQuery<HgSpendCategory>(SPEND_CATEGORY_QUERY, "spendCategory");
  const { data: productCategories, loading: productCategoriesLoading } =
    useTaxonomyQuery<HgProductCategory>(PRODUCT_CATEGORY_QUERY, "category");
  const { data: productAttributes, loading: productAttributesLoading } =
    useTaxonomyQuery<HgProductAttribute>(
      PRODUCT_ATTRIBUTE_QUERY,
      "productAttributes"
    );
  const { data: faiDepartments, loading: faiDepartmentsLoading } =
    useTaxonomyQuery<HgFaiDepartment>(
      GET_FAI_DEPARTMENTS_QUERY,
      "faiDepartment"
    );

  // Create data map for efficient Geo location lookups
  const countryDataMap = useMemo(() => {
    const map = new Map<string, HgCountry>();
    countries.forEach((item) => {
      if (item.alpha2) {
        map.set(item.alpha2, item);
      }
    });
    return map;
  }, [countries]);

  const regionDataMap = useMemo(() => {
    const map = new Map<string, HgRegion>();
    regions.forEach((item) => {
      if (item.name) {
        map.set(item.name, item);
      }
    });
    return map;
  }, [regions]);

  const stateDataMap = useMemo(() => {
    const map = new Map<string, HgState>();
    states.forEach((item) => {
      if (item.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [states]);

  // Create name maps for efficient ID to name lookups
  const sicNameMap = useMemo(() => {
    const map = new Map<string, string>();
    sic.forEach((item) => {
      if (item.id && item.name) {
        map.set(item.id, item.name);
      }
    });
    return map;
  }, [sic]);

  const naicsNameMap = useMemo(() => {
    const map = new Map<string, string>();
    naics.forEach((item) => {
      if (item.id && item.name) {
        map.set(item.id, item.name);
      }
    });
    return map;
  }, [naics]);

  const hgIndustryNameMap = useMemo(() => {
    const map = new Map<number, string>();
    hgIndustries?.forEach((item) => {
      if (item.id != null && item.name != null) {
        map.set(item.id, item.name);
      }
    });
    return map;
  }, [hgIndustries]);

  // Create FAI maps for efficient lookups
  const faiMaps = useMemo(() => {
    const faiDepartmentMap = new Map<string, HgFaiDepartment>();
    const faiRoleMap = new Map<
      string,
      { role: HgFaiRole; departmentId: string }
    >();
    const faiDepartmentRolesMap = new Map<string, string[]>();

    faiDepartments.forEach((dept) => {
      faiDepartmentMap.set(dept.id, dept);

      const roleIds: string[] = [];
      dept.roles.forEach((role) => {
        faiRoleMap.set(role.id, { role, departmentId: dept.id });
        roleIds.push(role.id);
      });

      faiDepartmentRolesMap.set(dept.id, roleIds);
    });

    return { faiDepartmentMap, faiRoleMap, faiDepartmentRolesMap };
  }, [faiDepartments]);

  return {
    products: products?.product.data ?? [],
    productsLoading,
    vendors,
    vendorsLoading,
    naics,
    sic,
    countries,
    regions,
    states,
    hgIndustries,
    spendCategories,
    productCategories,
    productAttributes,
    faiDepartments,
    sicNameMap,
    naicsNameMap,
    hgIndustryNameMap,
    countryDataMap,
    regionDataMap,
    stateDataMap,
    faiDepartmentMap: faiMaps.faiDepartmentMap,
    faiRoleMap: faiMaps.faiRoleMap,
    faiDepartmentRolesMap: faiMaps.faiDepartmentRolesMap,
    hgIndustriesLoading,
    naicsLoading,
    sicLoading,
    countriesLoading,
    regionsLoading,
    statesLoading,
    spendCategoriesLoading,
    spendCategoriesError,
    productCategoriesLoading,
    faiDepartmentsLoading,
    productAttributesLoading,
  };
};

// Create separate hooks for custom queries that can be used anywhere
export const useQueryStates = (
  variables: GenericVariablesObject
): { data: HgState[]; loading: boolean } => {
  const { data, loading } = useQuery(STATE_QUERY, {
    context: { clientName: TAXONOMY_CLIENT },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    variables,
    skip: !variables,
  });

  const statesData = (data?.state?.data as HgState[]) || [];

  const sortedData =
    statesData.length > 0
      ? sortData(statesData, "name" as keyof HgState & string, "asc")
      : statesData;

  return {
    data: sortedData,
    loading,
  };
};

// Exported so the public share page (ShareTaxonomyProvider) can provide its
// own, narrower value into the same context — every hook below keeps working
// unmodified against whichever provider is mounted.
export const TaxonomyContext = createContext<TaxonomyContextType | undefined>(
  undefined
);

export const TaxonomyProvider = ({ children }: { children: ReactNode }) => {
  const context = useTaxonomyContextFactory();
  return (
    <TaxonomyContext.Provider value={context}>
      {children}
    </TaxonomyContext.Provider>
  );
};

export const useTaxonomyContext = (): TaxonomyContextType => {
  const context = useContext(TaxonomyContext);
  if (!context) {
    throw new Error(
      "useTaxonomyContext must be used within a TaxonomyProvider"
    );
  }
  return context;
};
